import { useMemo } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  agencyClubsControllerAddClubV1,
  agencyClubsControllerFindOneV1,
  agencyClubsControllerListAgencyClubsV1,
  agencyClubsControllerRemoveClubV1,
  agencyClubsControllerUpdateClubV1,
} from '@/api/generated/agency-clubs/agency-clubs';
import { clubsControllerListClubsV1 } from '@/api/generated/clubs/clubs';
import {
  AgencyClubsControllerListAgencyClubsV1Relationship,
  ClubsControllerListClubsV1SortBy,
  ClubsControllerListClubsV1SortOrder,
  type AgencyClubDto,
  type AgencyClubsControllerListAgencyClubsV1Params,
  type CreateAgencyClubDto,
  type UpdateAgencyClubDto,
} from '@/api/generated/model';
import { sumMoney } from '@/shared/lib/format';
import { toAgencyClubsParams, type ClubsSearch } from '../lib/clubsSearch';

/**
 * Query key factory for the clubs module.
 *
 * Four independent roots under one prefix, because they invalidate on different
 * events. Adding a club changes the lists, the network summary *and* the
 * catalog (a row's `addedByAgency` flips to true), but no detail page exists
 * for it yet; editing one changes its detail page and the summary but leaves
 * the catalog alone. Invalidating `clubKeys.all` on every write would be
 * correct and wasteful — it would throw away every cached catalog search the
 * user made while filling in the form.
 */
export const clubKeys = {
  all: ['clubs'] as const,
  lists: () => [...clubKeys.all, 'list'] as const,
  list: (params: AgencyClubsControllerListAgencyClubsV1Params) =>
    [...clubKeys.lists(), params] as const,
  details: () => [...clubKeys.all, 'detail'] as const,
  detail: (id: string) => [...clubKeys.details(), id] as const,
  summary: () => [...clubKeys.all, 'summary'] as const,
  catalog: () => [...clubKeys.all, 'catalog'] as const,
  catalogSearch: (search: string) => [...clubKeys.catalog(), search] as const,
};

/**
 * The agency's clubs, one page at a time, appended as the user scrolls.
 *
 * Pagination is still entirely the server's: each fetch asks for one page and
 * `hasNextPage` off the response envelope decides whether another exists. The
 * only difference from a paged table is that pages accumulate in the cache
 * instead of replacing one another, which is what makes back-navigation to the
 * grid restore everything the user had already scrolled past.
 *
 * `page` is deliberately absent from the query key: it is the page *parameter*,
 * and including it would give every scroll position its own cache entry.
 */
export function useAgencyClubsInfiniteQuery(search: ClubsSearch) {
  const params = toAgencyClubsParams(search);

  return useInfiniteQuery({
    queryKey: clubKeys.list(params),
    queryFn: ({ pageParam, signal }) =>
      agencyClubsControllerListAgencyClubsV1({ ...params, page: pageParam }, { signal }),
    initialPageParam: search.page,
    getNextPageParam: (lastPage) =>
      lastPage.meta.hasNextPage ? lastPage.meta.page + 1 : undefined,
    // Keeps the current cards on screen while a filter change loads, so the
    // grid does not collapse to a spinner on every keystroke of the search box.
    placeholderData: (previous) => previous,
  });
}

export function useAgencyClubQuery(id: string) {
  return useQuery({
    queryKey: clubKeys.detail(id),
    queryFn: ({ signal }) => agencyClubsControllerFindOneV1(id, { signal }),
  });
}

/**
 * How many clubs the totals below are computed from.
 *
 * The API caps `limit` at 100 and there is no aggregate endpoint, so deals and
 * combined budget are summed over the first 100 clubs rather than all of them.
 * The counts are exact regardless — they come from `meta.total`, which counts
 * the whole match — and `isPartial` says when the money figures do not.
 *
 * This is the seam to delete when the backend grows a summary endpoint: replace
 * the sampled query with a single call and drop `isPartial`.
 */
const SUMMARY_SAMPLE_LIMIT = 100;

export interface ClubNetworkSummary {
  clubsInNetwork: number | undefined;
  strongRelationships: number | undefined;
  dealsCount: number | undefined;
  combinedBudget: string | undefined;
  budgetCurrency: string;
  /** Clubs whose budget is held in some other currency and is not in the total. */
  excludedByCurrency: number;
  /** True when the network is larger than the sample the money totals used. */
  isPartial: boolean;
  isLoading: boolean;
  error: unknown;
  refetch: () => void;
}

/**
 * The four figures above the grid.
 *
 * Deliberately not filtered by whatever the user has typed or selected: the
 * tiles describe the club network as a whole, so a search for "Milan" must not
 * make "Clubs in network" read 1. They are also two requests rather than one —
 * the strong-relationship count comes from `meta.total` on a filtered query,
 * which stays exact past the 100-club sample the money totals are capped at.
 */
export function useClubNetworkSummary(): ClubNetworkSummary {
  const network = useQuery({
    queryKey: clubKeys.summary(),
    queryFn: ({ signal }) =>
      agencyClubsControllerListAgencyClubsV1({ limit: SUMMARY_SAMPLE_LIMIT }, { signal }),
  });

  const strong = useQuery({
    queryKey: [...clubKeys.summary(), 'strong'] as const,
    queryFn: ({ signal }) =>
      agencyClubsControllerListAgencyClubsV1(
        { limit: 1, relationship: AgencyClubsControllerListAgencyClubsV1Relationship.strong },
        { signal },
      ),
  });

  const totals = useMemo(() => summarizeBudgets(network.data?.data ?? []), [network.data]);

  return {
    clubsInNetwork: network.data?.meta.total,
    strongRelationships: strong.data?.meta.total,
    dealsCount: network.data ? totals.dealsCount : undefined,
    combinedBudget: network.data ? totals.combinedBudget : undefined,
    budgetCurrency: totals.currency,
    excludedByCurrency: totals.excluded,
    isPartial: (network.data?.meta.total ?? 0) > (network.data?.data.length ?? 0),
    isLoading: network.isPending || strong.isPending,
    error: network.error ?? strong.error,
    refetch: () => {
      void network.refetch();
      void strong.refetch();
    },
  };
}

/**
 * Adds up deals and budgets across the sampled clubs.
 *
 * Budgets are Decimal strings and are summed as such — see `sumMoney`. They are
 * also grouped by currency first: adding a euro budget to a riyal one produces
 * a number that means nothing, so the largest currency group wins the tile and
 * the rest are reported as excluded rather than quietly folded in.
 */
function summarizeBudgets(clubs: readonly AgencyClubDto[]) {
  const byCurrency = new Map<string, string[]>();
  let dealsCount = 0;

  for (const club of clubs) {
    const window = club.transferWindow;
    if (!window) continue;

    dealsCount += window.dealsCount;
    const bucket = byCurrency.get(window.currency);
    if (bucket) bucket.push(window.budget);
    else byCurrency.set(window.currency, [window.budget]);
  }

  const groups = [...byCurrency.entries()].sort((a, b) => b[1].length - a[1].length);
  const dominant = groups[0];

  return {
    dealsCount,
    combinedBudget: dominant ? sumMoney(dominant[1]) : '0',
    currency: dominant?.[0] ?? 'EUR',
    excluded: groups.slice(1).reduce((count, [, budgets]) => count + budgets.length, 0),
  };
}

/** How many catalog rows the picker shows before the user narrows further. */
const CATALOG_PAGE_SIZE = 20;
/** Below this, a search matches most of the catalog and tells the user nothing. */
export const CATALOG_MIN_SEARCH_LENGTH = 2;

/**
 * The global club catalog, for the add-club picker.
 *
 * Idle until the user has typed: the catalog is 184 clubs across ten leagues,
 * so an unfiltered first page is an arbitrary twenty of them rather than a
 * useful starting point. It is also reference data seeded by a script, hence
 * the long `staleTime` — re-fetching it while someone types a name is pure
 * noise. `addedByAgency` on each row is the exception, and is why the whole
 * catalog cache is invalidated after a club is added or removed.
 */
export function useClubCatalogQuery(search: string) {
  const term = search.trim();

  return useQuery({
    queryKey: clubKeys.catalogSearch(term),
    queryFn: ({ signal }) =>
      clubsControllerListClubsV1(
        {
          search: term,
          limit: CATALOG_PAGE_SIZE,
          sortBy: ClubsControllerListClubsV1SortBy.name,
          sortOrder: ClubsControllerListClubsV1SortOrder.asc,
        },
        { signal },
      ),
    enabled: term.length >= CATALOG_MIN_SEARCH_LENGTH,
    staleTime: 5 * 60_000,
    placeholderData: (previous) => previous,
  });
}

/**
 * Adds a catalog club to the agency.
 *
 * Note this is an assignment, not a create — `clubId` must already exist in the
 * global catalog. The response is the complete `AgencyClubDto`, so the new card
 * needs no follow-up GET; it is seeded into the detail cache here so opening it
 * straight away renders instantly.
 */
export function useAddClubMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: CreateAgencyClubDto) => agencyClubsControllerAddClubV1(dto),
    onSuccess: async (club) => {
      queryClient.setQueryData(clubKeys.detail(club.id), club);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: clubKeys.lists() }),
        queryClient.invalidateQueries({ queryKey: clubKeys.summary() }),
        // `addedByAgency` just flipped on this club's catalog row.
        queryClient.invalidateQueries({ queryKey: clubKeys.catalog() }),
      ]);
    },
  });
}

/**
 * Updates the agency-level data on a club.
 *
 * `contacts` has replace semantics on the backend: sending the key replaces the
 * whole list, omitting it leaves contacts untouched. Contact ids are therefore
 * not stable across an update, so the response is written to the cache rather
 * than merged into what was there.
 */
export function useUpdateClubMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdateAgencyClubDto }) =>
      agencyClubsControllerUpdateClubV1(id, dto),
    onSuccess: async (club) => {
      queryClient.setQueryData(clubKeys.detail(club.id), club);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: clubKeys.lists() }),
        // A relationship change moves the strong-relationship count.
        queryClient.invalidateQueries({ queryKey: clubKeys.summary() }),
      ]);
    },
  });
}

export function useRemoveClubMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => agencyClubsControllerRemoveClubV1(id),
    onSuccess: async (_result, id) => {
      // Dropped rather than invalidated: refetching a detail page for a club
      // that no longer exists only produces a 404.
      queryClient.removeQueries({ queryKey: clubKeys.detail(id) });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: clubKeys.lists() }),
        queryClient.invalidateQueries({ queryKey: clubKeys.summary() }),
        queryClient.invalidateQueries({ queryKey: clubKeys.catalog() }),
      ]);
    },
  });
}
